import {
  ClientApiResponseSchema,
  ClientSchema,
  EmbeddedBranchApiResponseSchema,
  type CreateClientInput,
  type CreateBranchInput,
  ListClientApiResponsesSchema,
  type ListClientsQuery,
  type UpdateClientInput,
  type UpdateBranchInput,
} from "@recobra/schemas/client";

import { requestJson, requestVoid } from "./client";

type ListClientsOptions = Partial<ListClientsQuery>;

const MAX_CLIENT_LIST_LIMIT = 60;

export const clientsApi = {
  list: (opts?: ListClientsOptions) => {
    const q = new URLSearchParams();
    if (opts?.limit) q.set("limit", String(opts.limit));
    if (opts?.nextToken) q.set("nextToken", opts.nextToken);
    if (opts?.q) q.set("q", opts.q);
    if (opts?.status?.length) q.set("status", opts.status.join(","));
    if (opts?.companyType?.length) q.set("companyType", opts.companyType.join(","));
    if (opts?.sortBy) q.set("sortBy", opts.sortBy);
    if (opts?.sortOrder) q.set("sortOrder", opts.sortOrder);
    const qs = q.toString();

    // Lenient: restricted roles get a field-masked response from the same
    // endpoint — see ClientApiResponseSchema.
    return requestJson(`/clients${qs ? `?${qs}` : ""}`, ListClientApiResponsesSchema);
  },
  listAll: async (opts?: Omit<ListClientsOptions, "limit" | "nextToken">) => {
    const items: Awaited<ReturnType<typeof clientsApi.list>>["items"] = [];
    let nextToken: string | undefined;

    do {
      const page = await clientsApi.list({
        ...opts,
        limit: MAX_CLIENT_LIST_LIMIT,
        nextToken,
      });
      items.push(...page.items);
      nextToken = page.nextToken;
    } while (nextToken);

    return items;
  },
  get: (id: string) => requestJson(`/clients/${enc(id)}`, ClientApiResponseSchema),
  getEditable: (id: string) => requestJson(`/clients/${enc(id)}`, ClientSchema),
  create: (input: CreateClientInput) =>
    requestJson("/clients", ClientApiResponseSchema, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  update: (id: string, input: UpdateClientInput) =>
    requestJson(`/clients/${enc(id)}`, ClientApiResponseSchema, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  archive: (id: string) => requestVoid(`/clients/${enc(id)}`, { method: "DELETE" }),
  addClientNote: (id: string, text: string) =>
    requestJson(`/clients/${enc(id)}/notes`, ClientApiResponseSchema, {
      method: "POST",
      body: JSON.stringify({ text }),
    }),
  editClientNote: (id: string, noteId: string, text: string) =>
    requestJson(`/clients/${enc(id)}/notes/${noteId}`, ClientApiResponseSchema, {
      method: "PATCH",
      body: JSON.stringify({ text }),
    }),
  deleteClientNote: (id: string, noteId: string) =>
    requestJson(`/clients/${enc(id)}/notes/${noteId}`, ClientApiResponseSchema, {
      method: "DELETE",
    }),
  createBranch: (clientId: string, input: CreateBranchInput) =>
    requestJson(`/clients/${enc(clientId)}/branches`, EmbeddedBranchApiResponseSchema, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateBranch: (clientId: string, branchId: string, input: UpdateBranchInput) =>
    requestJson(
      `/clients/${enc(clientId)}/branches/${enc(branchId)}`,
      EmbeddedBranchApiResponseSchema,
      {
        method: "PUT",
        body: JSON.stringify(input),
      },
    ),
  archiveBranch: (clientId: string, branchId: string) =>
    requestVoid(`/clients/${enc(clientId)}/branches/${enc(branchId)}`, { method: "DELETE" }),
};

const enc = (id: string) => encodeURIComponent(id);
